import React, { useState } from "react";
import { useColorMode } from "@chakra-ui/react";
import {
  Box,
  Text,
  Flex,
  Button,
  VStack,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { PieChart, Pie, Cell, Tooltip as RechartTooltip } from "recharts";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import DetailLogList from "./DetailLogList";

export interface CheckInCheckOutData {
  checkInAddress: string;
  checkInPhotoUri: string;
  checkInTime: string;
  checkOutAddress: string;
  checkOutPhotoUri: string;
  checkOutTime: string;
  duration: string;
  isCheckedOut: boolean;
  taskUpdate?: string;
  workMode?: "Office" | "Home";
}

interface DonutChartStatusProps {
  durations: number[];
  entries: CheckInCheckOutData[];
}

const COLORS = ["#ECC94B", "#48BB78", "#F56565"]; // Yellow, Green, Red

const DonutChartStatus: React.FC<DonutChartStatusProps> = ({
  durations,
  entries,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const totalDuration = durations.reduce((a, b) => a + b, 0);

  const getStatus = () => {
    if (totalDuration === 0) return "No working today";
    if (totalDuration < 8) return "Underworking";
    if (totalDuration > 9) return "Burnout";
    return "Good";
  };

  const statusColor = {
    "No working today": "gray.500",
    Underworking: "#ECC94B", // 
    Good: "#48BB78", // 🟡 
    Burnout: "#F56565", // 
  };

  const worked = totalDuration;
  const remaining = 24 - worked;

  const formattedDuration =
    totalDuration === 0
      ? "0 min"
      : totalDuration < 1
      ? `${Math.round(totalDuration * 60)} min`
      : `${Math.floor(totalDuration)} hr ${Math.round(
          (totalDuration % 1) * 60
        )} min`;

  const chartData =
    worked === 0
      ? [{ name: "No Work", value: 24 }]
      : [
          { name: getStatus(), value: worked },
          { name: "Remaining", value: remaining },
        ];

  const chartColors =
    worked === 0 ? ["#CBD5E0"] : [statusColor[getStatus()], "#EDF2F7"];

  return (
    <Flex wrap="wrap" mt={6} gap={10}>
      {/* Left: Donut Chart */}
      <Box width="240px">
        <PieChart width={200} height={200}>
          <Pie
            data={chartData}
            dataKey="value"
            outerRadius={90}
            innerRadius={60}
            startAngle={90}
            endAngle={-270}
            isAnimationActive={true}
          >
            {chartData.map((_, idx) => (
              <Cell key={idx} fill={chartColors[idx]} />
            ))}
          </Pie>
          <RechartTooltip
            formatter={(value: number, name: string) => {
              const hrs = Math.floor(value);
              const mins = Math.round((value % 1) * 60);
              return [`${hrs} hr ${mins} min`, name];
            }}
          />
        </PieChart>

        <Flex justify="center" mt={2} fontSize="xs" gap={4}>
          <Text color="yellow.500">● Underworking</Text>
          <Text color="green.500">● Good</Text>
          <Text color="red.500">● Burnout</Text>
        </Flex>
      </Box>

      {/* Right: Tabs */}
      <Box flex="1" minW="300px">
        <Tabs size="sm" variant="enclosed">
          <TabList>
            <Tab _selected={{ color: isDark ? '#FBD282' : '#B59143', borderColor: '#E8C16C' }}>Summary</Tab>
            <Tab _selected={{ color: isDark ? '#FBD282' : '#B59143', borderColor: '#E8C16C' }}>Detail Logs</Tab>
          </TabList>

          <TabPanels mt={2}>
            {/* Summary Tab */}
            <TabPanel px={0}>
              <Flex
                flex="1"
                minW="300px"
                alignItems="center"
                justifyContent="center"
                bg="zs.card"
                borderRadius="md"
                borderWidth="1px"
                borderColor="zs.border"
                px={6}
                height={"25vh"}
                py={4}
              >
                <VStack spacing={2} textAlign="center">
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color={statusColor[getStatus()]}
                  >
                    {getStatus()}
                  </Text>

                  <Text mt={2}>
                    {getStatus() === "No working today" ? (
                      "User has not logged any work for the selected day."
                    ) : (
                      <>
                        User is{" "}
                        <Text
                          as="span"
                          color={statusColor[getStatus()]}
                          fontWeight="semibold"
                        >
                          {getStatus().toLowerCase()}
                        </Text>{" "}
                        today. <br />
                        Total duration:{" "}
                        <Text as="span" fontWeight="bold" color={isDark ? '#FBD282' : '#B59143'}>
                          {formattedDuration}
                        </Text>
                      </>
                    )}
                  </Text>
                </VStack>
              </Flex>
            </TabPanel>

            {/* Detail Logs Tab */}
            <TabPanel px={0}>
              <Box bg="zs.card" borderWidth="1px" borderColor="zs.border" borderRadius="md" px={4} py={3}>
                <DetailLogList entries={entries} />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Flex>
  );
};

export default DonutChartStatus;
